import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserProfile } from '../utils/useUserProfile';
import { useClubFitting } from '../utils/useClubFitting';
import { UserProfile } from '../utils/interfaces';
import RecommendationCards from '../components/RecommendationCards';
import AIAnalysis from '../components/AIAnalysis';
import AnalysisModal from '../components/profile/AnalysisModal';
import ProfileModal from '../components/profile/ProfileModal';
import TopBar from '../components/TopBar';
import SiteFooter from '../components/Footer';
import { analyzeUserProfile } from '../utils/useUserProfile';
import { GOLFER_PRODUCT_RESULTS_CTAS, GOLFER_PRODUCT_RESULTS_CTA_INTERVAL_MS } from '../utils/golfFittingCtas';
import './ClubFittingMobile.css';

const buildCtaSequence = () => {
  const rest = [...GOLFER_PRODUCT_RESULTS_CTAS.slice(1)];

  for (let i = rest.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [rest[i], rest[j]] = [rest[j], rest[i]];
  }

  return [GOLFER_PRODUCT_RESULTS_CTAS[0], ...rest];
};

const MobileClubFitting: React.FC = () => {
  const navigate = useNavigate();
  const {
    userProfile,
    profileLoading,
    editableProfile,
    handleProfileChange,
    handleProfileSave,
    isLoggedIn,
    isNewProfile,
    ensureEditableProfile,
  } = useUserProfile();

  const {
    recommendations,
    loading: recsLoading,
    error: recsError,
    hasFetched,
    overview,
    compare,
    fetchRecommendations,
  } = useClubFitting();

  const [analysis, setAnalysis] = useState<any | null>(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisRun, setAnalysisRun] = useState(false);
  const [isAnalysisModalOpen, setIsAnalysisModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [activeProfileCategory, setActiveProfileCategory] = useState<string | null>('Irons');
  const [selectedClub, setSelectedClub] = useState('Full Set');
  const [loadingTextIndex, setLoadingTextIndex] = useState(0);
  const [ctaSequence, setCtaSequence] = useState<string[]>(() => buildCtaSequence());
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [budget, setBudget] = useState('');
  const [selectedCondition, setSelectedCondition] = useState<'new' | 'used'>('new');
  const [lookupQuery, setLookupQuery] = useState('');
  const [profileHash, setProfileHash] = useState<string | null>(null);
  const [lastSubmittedPayload, setLastSubmittedPayload] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const generateProfileHash = (profile: any) => {
    if (!profile) return null;
    return JSON.stringify(profile);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const cachedAnalysis = localStorage.getItem('profileAnalysis');
    const cachedProfileHash = localStorage.getItem('profileHash');

    if (cachedAnalysis && cachedProfileHash) {
      try {
        setAnalysis(JSON.parse(cachedAnalysis));
        setProfileHash(cachedProfileHash);
        setAnalysisRun(true);
      } catch (error) {
        console.error('Failed to restore cached analysis:', error);
      }
    }
  }, []);

  useEffect(() => {
    if (userProfile) {
      const currentProfileHash = generateProfileHash(userProfile);

      if (profileHash && currentProfileHash !== profileHash) {
        setAnalysisRun(false);
        setAnalysis(null);
        localStorage.removeItem('profileAnalysis');
        localStorage.removeItem('profileHash');
      }

      setProfileHash(currentProfileHash);
    }
  }, [userProfile, profileHash]);

  useEffect(() => {
    if (!recsLoading) {
      return;
    }

    const interval = setInterval(() => {
      setLoadingTextIndex((prevIndex) => {
        const nextIndex = prevIndex + 1;

        if (nextIndex >= ctaSequence.length) {
          const nextSequence = buildCtaSequence();
          setCtaSequence(nextSequence);
          return 0;
        }

        return nextIndex;
      });
    }, GOLFER_PRODUCT_RESULTS_CTA_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [recsLoading, ctaSequence.length]);

  useEffect(() => {
    if (recsLoading) {
      const nextSequence = buildCtaSequence();
      setCtaSequence(nextSequence);
      setLoadingTextIndex(0);
    }
  }, [recsLoading]);

  const handleOpenMessenger = () => {
    // Messenger is not rendered on this page, so this is intentionally a no-op.
  };

  const handleAnalyzeProfile = async () => {
    if (!userProfile) return;
    setAnalysisLoading(true);
    setIsAnalysisModalOpen(true);
    setAnalysis(null);
    try {
      const result = await analyzeUserProfile(userProfile);
      const currentProfileHash = generateProfileHash(userProfile);

      setAnalysis(result);
      setProfileHash(currentProfileHash);
      setAnalysisRun(true);
      localStorage.setItem('profileAnalysis', JSON.stringify(result));
      if (currentProfileHash) {
        localStorage.setItem('profileHash', currentProfileHash);
      }
    } finally {
      setAnalysisLoading(false);
    }
  };

  const handleBudgetChange = (value: string) => {
    const sanitized = value.replace(/[^\d$]/g, '');
    setBudget(sanitized);
  };

  const handleGetRecommendations = () => {
    if (!userProfile) return;

    const finalProfilePayload = JSON.parse(JSON.stringify(userProfile));
    const nextRequestSignature = JSON.stringify({
      profile: finalProfilePayload,
      club: selectedClub,
      condition: selectedCondition,
      budget,
      lookupQuery,
    });

    if (lastSubmittedPayload && nextRequestSignature === lastSubmittedPayload) {
      setValidationError('Please update your player profile or game plan questions before requesting new recommendations.');
      return;
    }

    setValidationError(null);
    setLastSubmittedPayload(nextRequestSignature);

    const brandsToSend = undefined;

    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'get_recommendations_click', {
        club: selectedClub,
        condition: selectedCondition,
        budget: budget || 'not_set',
        lookup_query: lookupQuery || 'not_set',
        brands: 'not_set',
      });
    }

    fetchRecommendations(userProfile, isLoggedIn, selectedClub, brandsToSend, `$${budget}`, selectedCondition, lookupQuery || undefined);
  };

  const handleOpenProfileModal = (category: string = 'Irons') => {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'player_profile_click', {
        category: category,
      });
    }

    ensureEditableProfile();
    setActiveProfileCategory(category);
    setIsProfileModalOpen(true);
  };

  const handleCloseProfileModal = () => {
    setIsProfileModalOpen(false);
    setActiveProfileCategory(null);
  };

  const handleSaveProfileInModal = async () => {
    if (!editableProfile) return;
    try {
      await handleProfileSave();
      handleCloseProfileModal();
    } catch (error) {
      console.error('Failed to save profile:', error);
    }
  };

  const getProfileMenuCounts = (profile: UserProfile | null) => {
    const counts: Record<string, number> = {};
    if (!profile) return counts;

    const categories = ['Driver', 'Fairway Woods', 'Utilities', 'Irons', 'Wedges', 'Putter', 'Ball'] as const;

    categories.forEach((category) => {
      if (category === 'Driver') {
        counts[category] = profile.Driver ? 1 : 0;
      } else if (category === 'Fairway Woods') {
        const woodFields = ['3-Wood', '4-Wood', '5-Wood', '6-Wood', '7-Wood', '8-Wood', '9-Wood'];
        counts[category] = woodFields.filter((field) => {
          const value = profile[field as keyof UserProfile];
          return value !== undefined && value !== null && !(typeof value === 'string');
        }).length;
      } else if (category === 'Utilities') {
        const utilityFields = ['1-Iron', '2-Iron', '2-Hybrid', '3-Hybrid', '4-Hybrid', '5-Hybrid', '6-Hybrid'];
        counts[category] = utilityFields.filter((field) => {
          const value = profile[field as keyof UserProfile];
          return value !== undefined && value !== null && !(typeof value === 'string');
        }).length;
      } else if (category === 'Irons') {
        const ironFields = ['3-Iron', '4-Iron', '5-Iron', '6-Iron', '7-Iron', '8-Iron', '9-Iron', 'Pitching Wedge'];
        counts[category] = ironFields.filter((field) => {
          const value = profile[field as keyof UserProfile];
          return value !== undefined && value !== null && !(typeof value === 'string');
        }).length;
      } else if (category === 'Wedges') {
        const wedgeFields = ['Gap Wedge', 'Sand Wedge', 'Lob Wedge'];
        counts[category] = wedgeFields.filter((field) => {
          const value = profile[field as keyof UserProfile];
          return value !== undefined && value !== null && !(typeof value === 'string');
        }).length;
      } else if (category === 'Putter') {
        counts[category] = profile.Putter ? 1 : 0;
      } else if (category === 'Ball') {
        counts[category] = profile.Ball ? 1 : 0;
      }
    });

    return counts;
  };

  const profileMenuCounts = getProfileMenuCounts(userProfile as UserProfile | null);
  const shouldShowGetMyBagScoreAction = Boolean(userProfile) && !analysisRun;
  const shouldShowBagScoreAction = analysisRun && Boolean(analysis);

  const getFirstIncompleteProfileCategory = () => {
    const categories = ['Driver', 'Fairway Woods', 'Utilities', 'Irons', 'Wedges', 'Putter', 'Ball'] as const;

    const firstIncompleteCategory = categories.find((category) => {
      const count = profileMenuCounts[category] ?? 0;
      return count === 0;
    });

    if (firstIncompleteCategory) {
      return firstIncompleteCategory;
    }

    const ironsCount = profileMenuCounts.Irons ?? 0;
    const wedgesCount = profileMenuCounts.Wedges ?? 0;

    if (ironsCount < 4) {
      return 'Irons';
    }

    if (wedgesCount < 2) {
      return 'Wedges';
    }

    return 'General';
  };

  const handleContinueNextCategory = async (category: string) => {
    if (!editableProfile) return;
    try {
      await handleProfileSave();
      setActiveProfileCategory(category);
      setIsProfileModalOpen(true);
    } catch (error) {
      console.error('Failed to advance profile:', error);
    }
  };

  const hasSubmittedRecommendationRequest = Boolean(lastSubmittedPayload) || recsLoading || hasFetched || recommendations.length > 0;

  if (profileLoading) {
    return <div className="mobile-clubfitting loading">Loading your fitting profile…</div>;
  }

  return (
    <div className={`mobile-clubfitting${isDarkMode ? ' mobile-clubfitting--dark' : ''}`}>
      <TopBar
        showSignOut={isLoggedIn}
        isDarkMode={isDarkMode}
        onToggleTheme={() => setIsDarkMode((prev) => !prev)}
        onOpenProfileModal={handleOpenProfileModal}
        onOpenAnalysisModal={() => setIsAnalysisModalOpen(true)}
        onAnalyzeProfile={handleAnalyzeProfile}
        showGetMyBagScoreAction={shouldShowGetMyBagScoreAction}
        showBagScoreAction={shouldShowBagScoreAction}
        profileMenuCounts={profileMenuCounts}
        hideMenu={false}
        onSignOut={() => {
          localStorage.removeItem('token');
          sessionStorage.removeItem('token');
          localStorage.removeItem('messenger-conversation');
          localStorage.removeItem('messenger-visible');
          navigate('/');
        }}
      />

      <>
        <section className="mobile-clubfitting__card" style={{marginBottom: '-0.75rem', marginRight: '1rem', padding: '0.75rem'}}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '0.5rem', marginTop: '-0.5rem' }}>
              <h2 className="nf-section-title" style={{ color: 'rgb(211, 47, 47)', margin: 0 }}>Club Fitting</h2>
              {!isNewProfile ? (
                <span
                  onClick={() => handleOpenProfileModal(getFirstIncompleteProfileCategory())}
                  style={{
                    color: '#dc2626',
                    cursor: 'pointer',
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    background: '#fff5f5',
                    border: '1px solid rgba(220, 38, 38, 0.6)',
                    borderRadius: '999px',
                    padding: '0.12rem 0.45rem',
                    boxShadow: '0 2px 8px rgba(220, 38, 38, 0.18)',
                    display: 'inline-block',
                    lineHeight: 1.3,
                    transition: 'transform 0.15s ease, box-shadow 0.15s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.24)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(220, 38, 38, 0.18)';
                  }}
                >
                  Player Profile
                </span>
              ) : (
                <span
                  onClick={() => navigate('/basic-fitting')}
                  style={{
                    color: '#dc2626',
                    cursor: 'pointer',
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    background: '#fff5f5',
                    border: '1px solid rgba(220, 38, 38, 0.6)',
                    borderRadius: '999px',
                    padding: '0.12rem 0.45rem',
                    boxShadow: '0 2px 8px rgba(220, 38, 38, 0.18)',
                    display: 'inline-block',
                    lineHeight: 1.3,
                    transition: 'transform 0.15s ease, box-shadow 0.15s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.24)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(220, 38, 38, 0.18)';
                  }}
                >
                  Start Fitting Profile
                </span>
              )}
            </div>

            <p style={{
              marginTop: '-0.5rem',
              fontSize: '1rem',
              textAlign: 'left',
              color: 'black',
              padding: '0.5rem 0.5rem',
              lineHeight: 1.45
            }}>
              {hasSubmittedRecommendationRequest ? (
                <>Add more detail to <i>Profile</i> for a better fit.</>
              ) : (
                <>Add more detail to your <i>Profile</i> for a better fit — helping you gain distance, improve control, and play with more consistency.</>
              )}
            </p>
            <div className="mobile-clubfitting__filters">
              <label className="mobile-clubfitting__field">
                <select value={selectedClub} onChange={(e) => setSelectedClub(e.target.value)}>
                  <option>Full Club Set</option>
                  <option>Driver</option>
                  <option>Wood</option>
                  <option>Hybrid</option>
                  <option>Iron</option>
                  <option>Wedge</option>
                  <option>Putter</option>
                </select>
              </label>
              <label className="mobile-clubfitting__field">
                <input value={budget} onChange={(e) => handleBudgetChange(e.target.value)} placeholder="Budget, e.g. $500" />
              </label>
              <label className="mobile-clubfitting__field">
                <select value={selectedCondition} onChange={(e) => setSelectedCondition(e.target.value as 'new' | 'used')}>
                  <option value="new">Brand New</option>
                  <option value="used">Pre-Owned</option>
                </select>
              </label>
              <label className="mobile-clubfitting__field">
                <input value={lookupQuery} onChange={(e) => setLookupQuery(e.target.value)} placeholder="Keyword, e.g. Callaway Epic" />
              </label>
            </div>
            <button
              className="mobile-clubfitting__primary mobile-clubfitting__full nf-btn nf-btn-primary"
              style={{ marginTop: '0.5rem' }}
              onClick={handleGetRecommendations}
            >
              Get Recommendations
            </button>
            {(validationError || recsError) && (
              <p className="mobile-clubfitting__error" style={{ marginTop: '0.75rem' }}>
                {validationError ?? recsError}
              </p>
            )}
          </section>

          <ProfileModal
            isOpen={isProfileModalOpen}
            onClose={handleCloseProfileModal}
            activeCategory={activeProfileCategory}
            editableProfile={editableProfile ?? null}
            handleProfileChange={handleProfileChange}
            handleProfileSave={handleSaveProfileInModal}
            loading={profileLoading}
            onContinueNextCategory={handleContinueNextCategory}
          />

          <AnalysisModal
            isOpen={isAnalysisModalOpen}
            onClose={() => setIsAnalysisModalOpen(false)}
            analysis={analysis}
            loading={analysisLoading}
          />

          <section className="mobile-clubfitting__card">
            <AIAnalysis
              overview={overview}
              hasFetched={hasFetched}
              loading={recsLoading}
              onOpenMessenger={handleOpenMessenger}
              showLongTextToggle={hasSubmittedRecommendationRequest}
            />
          </section>

          <section className="mobile-clubfitting__card">
            {recsLoading && (
              <div className="mobile-clubfitting__loading-state" role="status" aria-live="polite">
                <span className="mobile-clubfitting__spinner" />
                <span>{ctaSequence[loadingTextIndex]}</span>
              </div>
            )}
            {recsError && <p className="mobile-clubfitting__error">{recsError}</p>}
            {!recsLoading && recommendations.length === 0 && <p></p>}
            <RecommendationCards recommendations={recommendations} compare={compare || undefined} />
          </section>
                
          <SiteFooter style={{ marginTop: '20px', width: '300px', alignSelf: 'center' }} />
        </>
    </div>
  );
};

export default MobileClubFitting;
